import { CSSProperties, FC } from 'react';
import { InputBase, InputProps, styled } from '@mui/material';

import './input.scss';
import { BACKGROUND, ORANGE, PEARL, PRIMARY, RED, STROKE, TEXT } from '../../Helpers/colors';
import Text from '../Text';

type InputTypes = 'text' | 'email' | 'hidden' | 'number' | 'password';

export type InputBackgroundType = 'primary' | 'base';

export interface InputProperties extends InputProps {
	backgroundType?: InputBackgroundType;
	className?: string;
	description?: string;
	error?: boolean;
	errorMessage?: string;
	label?: string;
	maxLength?: number;
	multiline?: boolean;
	onConfirm?: () => void;
	placeholder?: string;
	rowCount?: number;
	style?: CSSProperties;
	type?: InputTypes;
	warning?: boolean;
	warningMessage?: string;
}

export const backgroundColors = {
	primary: BACKGROUND.PRIMARY[100],
	base: PEARL.QUATERNARY,
};

export const focusBackgroundColors = {
	primary: BACKGROUND.PRIMARY[100],
	base: PRIMARY.QUATERNARY,
};

export const errorBackgroundColors = {
	primary: BACKGROUND.PRIMARY[100],
	base: RED.QUATERNARY,
};

export const warningBackgroundColors = {
	primary: BACKGROUND.PRIMARY[100],
	base: ORANGE.QUATERNARY,
};

export const disabledBackgroundColors = {
	primary: PEARL.QUATERNARY,
	base: BACKGROUND.PRIMARY[100],
};

export const CustomizedInput = styled(InputBase, {
	shouldForwardProp: (propName) => propName !== 'backgroundType' && propName !== 'warning',
})<InputProperties>(({ error, warning, backgroundType = 'primary', disabled }) => ({
	display: 'flex',
	padding: '10px 12px',
	alignItems: 'center',
	gap: '8px',
	alignSelf: 'stretch',
	borderRadius: '9px',
	border: error ? `1px solid ${RED.BASE}` : warning ? `1px solid ${ORANGE.BASE}` : `1px solid ${STROKE.SECONDARY}`,
	backgroundColor: disabled ? disabledBackgroundColors[backgroundType] : backgroundColors[backgroundType],
	width: '100%',
	minHeight: '40px',
	height: 'max-content',
	color: TEXT.PRIMARY,
	fontFamily: 'Inter',
	fontSize: '14px',
	fontWeight: 400,
	lineHeight: '20px',
	'&:hover': {
		boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.04)',
	},
	'&.Mui-focused': {
		boxShadow: `0px 0px 0px 4px ${error ? RED.TERTIARY : warning ? ORANGE.TERTIARY : PRIMARY.TERTIARY}`,
		border: error ? `1px solid ${RED.BASE}` : warning ? `1px solid ${ORANGE.BASE}` : `1.6px solid ${PRIMARY.BASE}`,
		backgroundColor: error
			? errorBackgroundColors[backgroundType]
			: warning
				? warningBackgroundColors[backgroundType]
				: focusBackgroundColors[backgroundType],
	},
	'& .MuiInputBase-input': {
		padding: '0',
		':focus': {
			backgroundColor: 'transparent',
		},
		'::placeholder': {
			color: TEXT.QUATERNARY,
		},
	},
	'& .MuiInputBase-inputMultiline': {
		paddingLeft: '4px',
	},
}));

const CustomInput: FC<InputProperties> = ({
	value,
	onChange,
	onBlur,
	error = false,
	errorMessage = '',
	warning = false,
	warningMessage = '',
	label = '',
	description = '',
	multiline = false,
	// rowCount = 2,
	className = '',
	type = 'text',
	placeholder,
	disabled = false,
	maxLength = 255,
	style,
	backgroundType = 'primary',
	startAdornment,
	endAdornment,
	onConfirm,
}) => (
	<div className={className}>
		<div className="inputLabelRow" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
			<Text
				className={`inputLabel ${label ? 'active' : ''}`}
				title={label}
				type="psm"
				fontWeight="medium"
				color={disabled ? TEXT.QUATERNARY : TEXT.PRIMARY}
			/>
			{warning && onConfirm && (
				<button type="button" className="confirmChip" onClick={onConfirm}>
					Confirm
				</button>
			)}
		</div>
		<CustomizedInput
			backgroundType={backgroundType}
			style={{ height: multiline ? 'max-content' : '40px', ...style }}
			type={type}
			value={value}
			placeholder={placeholder}
			onBlur={onBlur}
			onChange={onChange}
			error={error}
			warning={!error && warning}
			disabled={disabled}
			minRows={3}
			multiline={multiline}
			startAdornment={startAdornment}
			endAdornment={endAdornment}
			inputProps={{ maxLength: multiline ? 10000 : maxLength }}
			slotProps={{
				input: { maxLength: multiline ? 10000 : maxLength },
			}}
		/>
		<Text
			className={`error ${errorMessage ? 'active paddingTop8' : ''}`}
			title={errorMessage}
			type="psm"
			color={RED.DARK[16]}
		/>
		{!error && (
			<Text
				className={`warning ${warning && warningMessage ? 'active paddingTop8' : ''}`}
				title={warningMessage}
				type="psm"
				color={ORANGE.DARK[16]}
			/>
		)}
		<Text
			className={`inputDescription ${!error && !warning && Boolean(description) ? 'active paddingTop8' : ''}`}
			title={description}
			type="psm"
			color={disabled ? TEXT.QUATERNARY : TEXT.TERTIARY}
		/>
	</div>
);

export default CustomInput;
